/**
 * MeetingFormDialog Component
 * Form dialog for creating and editing meetings with multiple participants
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, Video, Phone, MapPin, Plus, Trash2 } from 'lucide-react';
import { Meeting, MeetingFormData, MeetingFormat, ContactRole, MeetingLocation, MeetingContact } from '@/types/calendar.types';
import { 
  MEETING_FORMAT_LABELS,
  DURATION_OPTIONS, 
  TIME_SLOTS,
  CALENDAR_TRANSLATIONS,
  CONTACT_ROLE_LABELS,
} from '@/constants/calendar.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AddressAutocomplete } from './AddressAutocomplete';
import { Separator } from '@/components/ui/separator';

interface MeetingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: MeetingFormData) => Promise<void>;
  meeting?: Meeting | null;
  defaultDealId?: string;
  isReschedule?: boolean;
}

const CONTACT_ROLES: ContactRole[] = [
  'internal', 'broker', 'buyer', 'seller', 'lender', 'consultant',
  'business_lawyer', 'immigration_lawyer', 'cpa', 'notary',
];

const emptyContact = (): MeetingContact => ({
  firstName: '', lastName: '', role: 'internal',
});

export function MeetingFormDialog({
  open,
  onOpenChange,
  onSubmit,
  meeting,
  defaultDealId,
  isReschedule = false,
}: MeetingFormDialogProps) {
  const { language } = useLanguage();
  const t = CALENDAR_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  const lang = language === 'fr' ? 'fr' : 'en';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contacts, setContacts] = useState<MeetingContact[]>([emptyContact()]);
  const [formData, setFormData] = useState<Partial<MeetingFormData>>({
    title: '',
    description: '',
    dealId: defaultDealId || '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    duration: 30,
    type: 'internal',
    format: 'video',
    participants: [],
    location: 'acquizgroup',
    externalAddress: '',
  });

  useEffect(() => {
    if (meeting) {
      // Build contacts list from meeting data
      const existingContacts = meeting.contacts?.length
        ? meeting.contacts
        : meeting.contact
          ? [meeting.contact]
          : [emptyContact()];

      setContacts(existingContacts);
      setFormData({
        title: meeting.title,
        description: meeting.description || '',
        dealId: meeting.dealId || '',
        date: isReschedule ? format(new Date(), 'yyyy-MM-dd') : meeting.date,
        time: isReschedule ? '09:00' : meeting.time,
        duration: meeting.duration,
        type: meeting.type,
        format: meeting.format || 'video',
        participants: meeting.participants,
        location: meeting.location || 'acquizgroup',
        externalAddress: meeting.externalAddress || '',
      });
    } else {
      setContacts([emptyContact()]);
      setFormData({
        title: '',
        description: '',
        dealId: defaultDealId || '',
        date: format(new Date(), 'yyyy-MM-dd'),
        time: '09:00',
        duration: 30,
        type: 'internal',
        format: 'video',
        participants: [],
        location: 'acquizgroup',
        externalAddress: '',
      });
    }
  }, [meeting, defaultDealId, open, isReschedule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.date || !formData.time) return;
    if (!contacts[0]?.firstName || !contacts[0]?.lastName) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        contact: contacts[0],
        contacts,
      } as MeetingFormData);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateContact = (index: number, field: string, value: string) => {
    setContacts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const addContact = () => {
    setContacts(prev => [...prev, emptyContact()]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= 1) return;
    setContacts(prev => prev.filter((_, i) => i !== index));
  };

  const showExternalFields = formData.format === 'in_person' && formData.location === 'external';

  const dialogTitle = isReschedule
    ? (lang === 'fr' ? 'Reprogrammer le rendez-vous' : 'Reschedule Meeting')
    : meeting
      ? t.editMeeting
      : t.newMeeting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-4">
          <form onSubmit={handleSubmit} className="space-y-4" id="meeting-form">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{t.meetingTitle} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={lang === 'fr' ? 'Ex: Appel découverte' : 'Ex: Discovery call'}
                required
              />
            </div>

            {/* ═══ PARTICIPANTS ═══ */}
            {contacts.map((contact, index) => (
              <div key={index} className="space-y-3">
                {index > 0 && <Separator className="my-2" />}
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">
                    {lang === 'fr' ? `Participant ${index + 1}` : `Participant ${index + 1}`}
                    {index === 0 && ' *'}
                  </Label>
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => removeContact(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Name */}
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    value={contact.firstName}
                    onChange={(e) => updateContact(index, 'firstName', e.target.value)}
                    placeholder={lang === 'fr' ? 'Prénom' : 'First name'}
                    required={index === 0}
                  />
                  <Input
                    value={contact.lastName}
                    onChange={(e) => updateContact(index, 'lastName', e.target.value)}
                    placeholder={lang === 'fr' ? 'Nom' : 'Last name'}
                    required={index === 0}
                  />
                </div>

                {/* Role */}
                <Select
                  value={contact.role}
                  onValueChange={(value) => updateContact(index, 'role', value)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTACT_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {CONTACT_ROLE_LABELS[role][lang]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Company, Phone, Email */}
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={contact.company || ''}
                    onChange={(e) => updateContact(index, 'company', e.target.value)}
                    placeholder={lang === 'fr' ? 'Compagnie' : 'Company'}
                    className="text-sm"
                  />
                  <Input
                    value={contact.phone || ''}
                    onChange={(e) => updateContact(index, 'phone', e.target.value)}
                    placeholder="(514) 555-1234"
                    className="text-sm"
                  />
                  <Input
                    type="email"
                    value={contact.email || ''}
                    onChange={(e) => updateContact(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    className="text-sm"
                  />
                </div>
              </div>
            ))}

            {/* Add Participant Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addContact}
              className="w-full gap-2 border-dashed"
            >
              <Plus className="w-4 h-4" />
              {lang === 'fr' ? 'Ajouter un participant' : 'Add Participant'}
            </Button>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t.description}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={lang === 'fr' ? 'Détails du rendez-vous...' : 'Meeting details...'}
                rows={2}
              />
            </div>

            {/* Meeting Format */}
            <div className="space-y-2">
              <Label>{lang === 'fr' ? 'Format' : 'Format'}</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={formData.format === 'video' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, format: 'video' })}
                  size="sm"
                  className="gap-1.5"
                >
                  <Video className="w-4 h-4" />
                  {MEETING_FORMAT_LABELS.video[lang]}
                </Button>
                <Button
                  type="button"
                  variant={formData.format === 'phone' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, format: 'phone' })}
                  size="sm"
                  className="gap-1.5"
                >
                  <Phone className="w-4 h-4" />
                  {MEETING_FORMAT_LABELS.phone[lang]}
                </Button>
                <Button
                  type="button"
                  variant={formData.format === 'in_person' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, format: 'in_person' })}
                  size="sm"
                  className="gap-1.5"
                >
                  <MapPin className="w-4 h-4" />
                  {MEETING_FORMAT_LABELS.in_person[lang]}
                </Button>
              </div>
            </div>

            {/* Location (only for in-person) */}
            {formData.format === 'in_person' && (
              <div className="space-y-2">
                <Label>{lang === 'fr' ? 'Lieu' : 'Location'}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={formData.location === 'acquizgroup' ? 'default' : 'outline'}
                    onClick={() => setFormData({ ...formData, location: 'acquizgroup' })}
                    size="sm"
                  >
                    AcquizGroup
                  </Button>
                  <Button
                    type="button"
                    variant={formData.location === 'external' ? 'default' : 'outline'}
                    onClick={() => setFormData({ ...formData, location: 'external' })}
                    size="sm"
                  >
                    {lang === 'fr' ? 'Extérieur' : 'External'}
                  </Button>
                </div>
              </div>
            )}

            {/* External Address */}
            {showExternalFields && (
              <div className="space-y-2">
                <Label>{lang === 'fr' ? 'Adresse du rendez-vous' : 'Meeting Address'}</Label>
                <AddressAutocomplete
                  value={formData.externalAddress || ''}
                  onChange={(val) => setFormData({ ...formData, externalAddress: val })}
                  language={lang}
                  placeholder={lang === 'fr' ? 'Rechercher une adresse...' : 'Search address...'}
                />
              </div>
            )}

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.date} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !formData.date && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.date ? format(new Date(formData.date + 'T12:00:00'), 'PPP') : t.date}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.date ? new Date(formData.date + 'T12:00:00') : undefined}
                      onSelect={(date) => setFormData({ 
                        ...formData, 
                        date: date ? format(date, 'yyyy-MM-dd') : '' 
                      })}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>{t.time} *</Label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label>{t.duration}</Label>
              <Select
                value={formData.duration?.toString()}
                onValueChange={(value) => setFormData({ ...formData, duration: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t.cancel}
          </Button>
          <Button type="submit" form="meeting-form" disabled={isSubmitting}>
            {isSubmitting 
              ? '...' 
              : isReschedule 
                ? (lang === 'fr' ? 'Reprogrammer' : 'Reschedule')
                : t.save
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
